import React, { useEffect, useCallback, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { Modal } from '../../../common/Modal';
import { useAdminTranslation } from '../../../../modules/i18n';
import { useSimplifiedUploadWizard } from './hooks/useSimplifiedUploadWizard';
import { StepIndicator } from '../../../common';
import { ConfirmDialog } from '../../../Notification';
import { FolderDropStep } from './components/FolderDropStep';
import { ProcessingStep } from './components/ProcessingStep';
import { useParsedUploadApi, ApiError } from '../../../../contexts/ApiContext';
import { ParsedUploadProgressEvent } from '../../../../types/upload';
import { CustomArchiveBuilder, FileReadError } from '../../../../utils/customArchive';

interface SimplifiedParsedUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  onComplete?: () => void;
}

export const SimplifiedParsedUploadModal: React.FC<SimplifiedParsedUploadModalProps> = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  onComplete
}) => {
  const { t } = useAdminTranslation();
  const wizard = useSimplifiedUploadWizard();
  const api = useParsedUploadApi();
  const [progressEvent, setProgressEvent] = useState<ParsedUploadProgressEvent | null>(null);

  // Upload state
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'validating' | 'uploading' | 'completed' | 'failed'>('idle');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Reset wizard when modal closes
  useEffect(() => {
    if (!isOpen) {
      wizard.reset();
      setProgressEvent(null);
      setUploadStatus('idle');
      setShowCloseConfirm(false);
    }
  }, [isOpen, wizard]);

  // Handle close — dialog-gated while processing, immediate otherwise
  const handleClose = useCallback(() => {
    const isProcessing = uploadStatus === 'uploading' || uploadStatus === 'validating';
    if (isProcessing) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }, [uploadStatus, onClose]);

  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    onClose();
  }, [onClose]);

  // Handle the upload process
  const handleUpload = useCallback(async () => {
    if (uploadStatus !== 'idle') return;

    setUploadStatus('uploading');
    wizard.goToStep(2);
    wizard.setProcessingState('processing', 20, t('content.documents.wizard.validation.buildingArchive'));

    try {
      const archiveBuilder = new CustomArchiveBuilder();
      const archiveBlob = await archiveBuilder.buildParsedArchive(
        wizard.state.markdownFiles.map((f) => f.file),
        wizard.state.assetFiles.map((f) => f.file),
        wizard.state.sheetFiles.map((f) => f.file),
        (progress) => {
          wizard.setProcessingState('processing', 20 + Math.round(progress * 0.3), '');
        }
      );

      wizard.setProcessingState('processing', 50, t('content.documents.wizard.validation.uploading'));

      // Resolve folder name: when uploading at root (no folderId), use the
      // dropped folder's actual name from webkitRelativePath instead of the
      // generic placeholder. (2026-05-07)
      const dropRoot = (() => {
        const firstWithPath = wizard.state.markdownFiles.find((f) => f.relativePath)
          ?? wizard.state.assetFiles.find((f) => f.relativePath)
          ?? wizard.state.sheetFiles.find((f) => f.relativePath);
        const rel = firstWithPath?.relativePath;
        if (!rel) return undefined;
        const root = rel.split('/')[0];
        return root && root !== rel ? root : undefined;
      })();
      const effectiveFolderName = folderId ? folderName : (dropRoot ?? folderName);

      // Upload the archive
      const result = await api.uploadArchive(archiveBlob, folderId, effectiveFolderName);

      wizard.setProcessingState('complete', 100, t('content.documents.wizard.processing.complete'));
      wizard.setUploadResult(result);
      setUploadStatus('completed');

      // Store results for display
      setProgressEvent({
        session_id: 'single-shot',
        phase: 'completed',
        overall_progress: 100,
        current_file: '',
        files_processed: wizard.state.markdownFiles.length,
        files_total: wizard.state.markdownFiles.length,
        folders_created: result.folders_created,
        documents_created: result.documents_created,
        assets_uploaded: result.assets_uploaded,
        assets_deduplicated: result.assets_deduplicated,
        errors: result.errors.map((e) => ({ code: 10000, message: e })),
        warnings: result.warnings,
        status: result.success ? 'COMPLETED' : 'FAILED'
      });
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus('failed');

      if (error instanceof FileReadError) {
        wizard.setProcessingState('error', 0, error.message);
      } else if (error instanceof ApiError) {
        wizard.setProcessingState('error', 0, error.message);
      } else {
        const message = error instanceof Error ? error.message : 'Upload failed';
        wizard.setProcessingState('error', 0, message);
      }
    }
  }, [wizard, api, folderId, uploadStatus, t]);

  // Handle completion
  const handleComplete = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Render step content
  const renderStepContent = () => {
    switch (wizard.state.currentStep) {
      case 1:
        return (
          <FolderDropStep
            markdownFiles={wizard.state.markdownFiles}
            assetFiles={wizard.state.assetFiles}
            sheetFiles={wizard.state.sheetFiles}
            validationErrors={wizard.state.validationErrors}
            validationWarnings={wizard.state.validationWarnings}
            onFolderDrop={wizard.handleFolderDrop}
            onClearFiles={wizard.clearFiles}
          />
        );
      case 2:
        return (
          <ProcessingStep
            phase={wizard.state.processingPhase}
            progress={wizard.state.processingProgress}
            message={wizard.state.processingMessage}
            progressEvent={progressEvent}
            onComplete={handleComplete}
            onCancel={handleCancel}
          />
        );
      default:
        return null;
    }
  };

  const isProcessing = uploadStatus === 'validating' || uploadStatus === 'uploading';
  const isComplete = wizard.state.processingPhase === 'complete';

  const isAutoCreateMode = !folderId;
  const displayFolderName = isAutoCreateMode
    ? t('content.documents.wizard.autoCreatePlaceholder')
    : folderName;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('content.documents.wizard.title')}
      headerContent={
        <p className='-mt-2 px-3 pb-3 text-sm text-gray-600 dark:text-dark-text-secondary sm:px-4 sm:pb-4'>
          {isAutoCreateMode
            ? t('content.documents.wizard.autoCreateHint')
            : (<>{t('content.documents.wizard.targetFolder')}:{' '}
                <span className='font-medium text-primary'>"{displayFolderName}"</span>
              </>)
          }
        </p>
      }
      size='lg'
      height='lg'
      footer={
        <div className='flex w-full items-center justify-end space-x-4'>
          <button
            onClick={() => {
              if (isComplete) {
                handleComplete(); // Refresh data before closing
              }
              handleClose();
            }}
            disabled={isProcessing}
            className='rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-border-dark dark:bg-dark-background-secondary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
          >
            {isComplete ? t('content.documents.wizard.buttons.done') : t('content.documents.wizard.buttons.cancel')}
          </button>
          {/* Step 1: Upload button */}
          {wizard.state.currentStep === 1 && (
            <button
              onClick={handleUpload}
              disabled={!wizard.canProceed() || isProcessing}
              className='inline-flex items-center space-x-2 rounded-lg bg-gradient-to-r from-primary to-primary/90 px-8 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100'
            >
              <span>{t('content.documents.wizard.buttons.upload')}</span>
              <ArrowRightIcon className='h-4 w-4' />
            </button>
          )}
        </div>
      }
    >
      <div className='space-y-6'>
        {/* Step indicator */}
        <StepIndicator
          currentStep={wizard.state.currentStep}
          steps={[
            { label: t('content.documents.wizard.steps.selectFiles') },
            { label: t('content.documents.wizard.steps.upload') }
          ]}
        />

        {/* Step content */}
        {renderStepContent()}
      </div>
      <ConfirmDialog
        isOpen={showCloseConfirm}
        title={t('content.documents.wizard.confirmCloseTitle')}
        message={t('content.documents.wizard.confirmClose')}
        confirmText={t('content.documents.wizard.confirmCloseButton')}
        cancelText={t('content.documents.wizard.keepProcessing')}
        confirmVariant='danger'
        onConfirm={confirmClose}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </Modal>
  );
};

export default SimplifiedParsedUploadModal;
